import React from 'react';
import { Send } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useContactForm, isTurnstileEnabled, turnstileSiteKey } from '../hooks/useContactForm';

const ContactForm = () => {
  const [sectionRef, isSectionVisible] = useScrollReveal();
  const { formData, isSubmitting, submitStatus, handleChange, handleSubmit, turnstileRef } = useContactForm();

  return (
    <section id="contact" className="py-20 lg:py-28 bg-gray-50 relative overflow-hidden">
      {/* Handstand mascot - bottom right decorative */}
      <img
        src="/immi-handstand-nobg.webp"
        alt="Immi mascot handstand"
        className="absolute right-4 lg:right-12 bottom-4 w-24 lg:w-32 h-auto hidden lg:block opacity-70"
        style={{ animation: 'mascot-float 5s ease-in-out infinite 1s', filter: 'drop-shadow(0 8px 20px rgba(0,188,228,0.3))' }}
      />
      <div
        ref={sectionRef}
        className={`section-container reveal ${isSectionVisible ? 'visible' : ''}`}
      >
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left Content */}
          <div>
            <span
              className="block text-sm font-semibold text-[#00BCE4] uppercase tracking-[3px] mb-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Get in touch
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Feel free to contact<br />
              <span className="text-[#00BCE4]">us directly...</span>
            </h2>
            <p
              className="text-base text-gray-500 leading-relaxed mb-8"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              If you've got a question you want to ask us, or you want to know more about how we can
              streamline your print business, drop us a line using the form below.
            </p>

            {/* Contact Info Cards */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-white border border-gray-200">
                <div className="w-12 h-12 rounded-lg bg-[#00BCE4]/10 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00BCE4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                </div>
                <div>
                  <span className="block text-xs text-gray-400 font-medium uppercase tracking-wider"
                    style={{ fontFamily: 'Outfit, sans-serif' }}>Phone</span>
                  <span className="text-sm font-semibold text-gray-900"
                    style={{ fontFamily: 'Inter, sans-serif' }}>+44 (0)1245 231670</span>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-white border border-gray-200">
                <div className="w-12 h-12 rounded-lg bg-[#00BCE4]/10 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00BCE4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                </div>
                <div>
                  <span className="block text-xs text-gray-400 font-medium uppercase tracking-wider"
                    style={{ fontFamily: 'Outfit, sans-serif' }}>Address</span>
                  <span className="text-sm font-semibold text-gray-900"
                    style={{ fontFamily: 'Inter, sans-serif' }}>27 Fairycroft Road, Saffron Walden, CB10 1LZ</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-5 sm:p-8 lg:p-10 shadow-sm border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2"
                      style={{ fontFamily: 'Outfit, sans-serif' }}>Name *</label>
                    <input
                      type="text"
                      name="name"
                      id="contact-name"
                      aria-label="Your name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:border-[#00BCE4] focus:ring-2 focus:ring-[#00BCE4]/20 transition-all duration-300 outline-none"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2"
                      style={{ fontFamily: 'Outfit, sans-serif' }}>Email *</label>
                    <input
                      type="email"
                      name="email"
                      id="contact-email"
                      aria-label="Your email address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:border-[#00BCE4] focus:ring-2 focus:ring-[#00BCE4]/20 transition-all duration-300 outline-none"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2"
                      style={{ fontFamily: 'Outfit, sans-serif' }}>Company</label>
                    <input
                      type="text"
                      name="company"
                      id="contact-company"
                      aria-label="Your company name"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:border-[#00BCE4] focus:ring-2 focus:ring-[#00BCE4]/20 transition-all duration-300 outline-none"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2"
                      style={{ fontFamily: 'Outfit, sans-serif' }}>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      id="contact-phone"
                      aria-label="Your phone number"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:border-[#00BCE4] focus:ring-2 focus:ring-[#00BCE4]/20 transition-all duration-300 outline-none"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3"
                    style={{ fontFamily: 'Outfit, sans-serif' }}>Do you currently have an MIS system?</label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="hasMIS"
                        value="yes"
                        checked={formData.hasMIS === 'yes'}
                        onChange={handleChange}
                        className="w-4 h-4 text-[#00BCE4] border-gray-300 focus:ring-[#00BCE4]"
                      />
                      <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors"
                        style={{ fontFamily: 'Inter, sans-serif' }}>Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="hasMIS"
                        value="no"
                        checked={formData.hasMIS === 'no'}
                        onChange={handleChange}
                        className="w-4 h-4 text-[#00BCE4] border-gray-300 focus:ring-[#00BCE4]"
                      />
                      <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors"
                        style={{ fontFamily: 'Inter, sans-serif' }}>No</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}>Message</label>
                  <textarea
                    id="contact-message"
                    aria-label="Your message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:border-[#00BCE4] focus:ring-2 focus:ring-[#00BCE4]/20 transition-all duration-300 outline-none resize-none"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>

                {/* Newsletter opt-in (GDPR) */}
                <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-4">
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="contact-subscribe-label">
                    <input
                      type="checkbox"
                      name="subscribe"
                      checked={formData.subscribe}
                      onChange={handleChange}
                      data-testid="contact-subscribe-checkbox"
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#00BCE4] focus:ring-[#00BCE4]"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors leading-relaxed"
                      style={{ fontFamily: 'Inter, sans-serif' }}>
                      Add me to the Imprint MIS newsletter for product updates and industry insights.
                      <span className="block text-xs text-gray-400 mt-1">
                        You can unsubscribe at any time. See our Privacy Policy.
                      </span>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                  className="btn-primary w-full justify-center text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : "Let's Talk"}
                  <Send size={16} />
                </button>

                {/* Cloudflare Turnstile — invisible CAPTCHA */}
                {isTurnstileEnabled && (
                  <div className="cf-turnstile-wrapper w-full overflow-hidden flex flex-col items-center" data-testid="cf-turnstile-wrapper">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={turnstileSiteKey}
                      options={{
                        size: 'invisible',
                        action: 'contact',
                        theme: 'light',
                      }}
                    />
                    <p className="text-[10px] text-gray-400 mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Protected by Cloudflare Turnstile
                    </p>
                  </div>
                )}

                {/* Success/Error Message */}
                {submitStatus && (
                  <div className={`p-4 rounded-lg ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <p className={`text-sm ${
                      submitStatus.type === 'success' ? 'text-green-700' : 'text-red-700'
                    }`} style={{ fontFamily: 'Inter, sans-serif' }}>
                      {submitStatus.message}
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    );
  };
  
  export default ContactForm;
